import { useState, useRef, useEffect, useCallback } from 'react'
import api from '../services/api'
import { useIbsCbsDescricoes } from '../hooks/useIbsCbsDescricoes'

interface Regra {
  ibs08_cod: number
  ibs08_des: string
  ibs08_cst?: string
  ibs08_tri?: string
  ibs08_dfe?: string
}

interface Props {
  codValue: string
  desValue: string
  onChange: (cod: string, des: string) => void
  // Restringe a busca/lista só às regras com esse(s) cClassTrib (ex.: tipo 07 só aceita 410030)
  triPermitidos?: string[]
  // Restringe a busca/lista só às regras cadastradas pra esse Documento Fiscal (ex.: '99' = NFSe)
  docFiscalPermitido?: string
}

export default function IbsCbsSelect({ codValue, desValue, onChange, triPermitidos, docFiscalPermitido }: Props) {
  const { cstDes, classTribDes } = useIbsCbsDescricoes()
  const [aberta, setAberta] = useState(false)
  const [filtro, setFiltro] = useState('')
  const [resultados, setResultados] = useState<Regra[]>([])
  const [buscando, setBuscando] = useState(false)
  const [codDigitado, setCodDigitado] = useState(codValue)
  const [aviso, setAviso] = useState('')
  const [triSelecionado, setTriSelecionado] = useState('')
  const [cstSelecionado, setCstSelecionado] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => { setCodDigitado(codValue); if (codValue) setAviso('') }, [codValue])

  // Busca o cClassTrib/CST da regra já selecionada (ex.: ao restaurar uma nota salva),
  // já que codValue/desValue não carregam esses dados.
  useEffect(() => {
    if (!codValue) { setTriSelecionado(''); setCstSelecionado(''); return }
    api.get('/erp/ibscbs', { params: { busca: codValue } }).then(r => {
      const encontrado = r.data.find((x: Regra) => String(x.ibs08_cod) === String(codValue))
      if (encontrado) { setTriSelecionado(encontrado.ibs08_tri || ''); setCstSelecionado(encontrado.ibs08_cst || '') }
    }).catch(() => {})
  }, [codValue])

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberta(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const buscar = useCallback((valor: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setBuscando(true)
      try {
        const r = await api.get('/erp/ibscbs', { params: { busca: valor } })
        let dados = triPermitidos ? r.data.filter((x: Regra) => triPermitidos.includes(x.ibs08_tri || '')) : r.data
        if (docFiscalPermitido) dados = dados.filter((x: Regra) => x.ibs08_dfe === docFiscalPermitido)
        setResultados(dados)
      } finally {
        setBuscando(false)
      }
    }, 200)
  }, [triPermitidos, docFiscalPermitido])

  async function buscarPorCodigo(cod: string) {
    if (!cod.trim()) { onChange('', ''); setAviso(''); return }
    try {
      const r = await api.get('/erp/ibscbs', { params: { busca: cod.trim() } })
      const encontrado = r.data.find((x: Regra) => String(x.ibs08_cod) === cod.trim())
      if (encontrado && (!triPermitidos || triPermitidos.includes(encontrado.ibs08_tri || '')) && (!docFiscalPermitido || encontrado.ibs08_dfe === docFiscalPermitido)) {
        onChange(String(encontrado.ibs08_cod), encontrado.ibs08_des)
        setCodDigitado(String(encontrado.ibs08_cod))
        setTriSelecionado(encontrado.ibs08_tri || '')
        setCstSelecionado(encontrado.ibs08_cst || '')
        setAviso('')
      } else {
        onChange('', '')
        setAviso(`Código "${cod.trim()}" não encontrado`)
      }
    } catch {
      onChange('', '')
      setAviso(`Código "${cod.trim()}" não encontrado`)
    }
  }

  function selecionar(r: Regra) {
    onChange(String(r.ibs08_cod), r.ibs08_des)
    setCodDigitado(String(r.ibs08_cod))
    setTriSelecionado(r.ibs08_tri || '')
    setCstSelecionado(r.ibs08_cst || '')
    setAviso('')
    setAberta(false)
    setFiltro('')
    setResultados([])
  }

  return (
    <div className="nat-wrap" ref={ref}>
      <div className="nat-input-row">
        <input
          className="nat-cod-input"
          type="text"
          placeholder="Cód."
          value={codDigitado}
          style={aviso ? { borderColor: '#dc2626' } : undefined}
          onChange={e => { setCodDigitado(e.target.value.replace(/\D/g, '')); setAviso('') }}
          onBlur={e => {
            if (e.target.value) buscarPorCodigo(e.target.value)
            else { onChange('', ''); setAviso('') }
          }}
          onKeyDown={e => { if (e.key === 'Enter') buscarPorCodigo(codDigitado) }}
        />
        <div className="nat-desc-box" onClick={() => { setAberta(o => { if (!o) buscar(''); return !o }); setFiltro(''); setResultados([]) }}>
          {desValue
            ? <span className="nat-dsc">{desValue}</span>
            : <span className="nat-placeholder">Selecione a regra IBS/CBS...</span>
          }
          <span className="nat-chevron">{aberta ? '▴' : '▾'}</span>
        </div>
      </div>
      {aviso && <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.2rem', paddingLeft: '0.25rem' }}>{aviso}</div>}
      {desValue && triSelecionado && (
        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151', marginTop: '0.2rem', paddingLeft: '0.25rem', lineHeight: 1.35 }}>
          <div>CST {cstSelecionado}{cstDes(cstSelecionado) ? ` – ${cstDes(cstSelecionado)}` : ''}</div>
          <div>cClassTrib {triSelecionado}{classTribDes(cstSelecionado, triSelecionado) ? ` – ${classTribDes(cstSelecionado, triSelecionado)}` : ''}</div>
        </div>
      )}

      {aberta && (
        <div className="nat-lista">
          <div className="nat-filtro-wrap">
            <input
              autoFocus
              className="nat-filtro"
              placeholder="Código ou descrição..."
              value={filtro}
              onChange={e => { setFiltro(e.target.value); buscar(e.target.value) }}
            />
          </div>
          <div className="nat-itens">
            {buscando && <div className="nat-vazio">Buscando...</div>}
            {!buscando && resultados.length === 0 && (
              <div className="nat-vazio">Nenhum resultado</div>
            )}
            {resultados.map(r => (
              <button
                key={r.ibs08_cod}
                className={`nat-item${String(r.ibs08_cod) === codValue ? ' nat-item--ativo' : ''}`}
                onClick={() => selecionar(r)}
              >
                <div className="nat-item-linha1">
                  <span className="nat-item-cod">#{r.ibs08_cod}</span>
                  {r.ibs08_tri && <span style={{ fontSize:'0.85rem', fontWeight:700, color:'#374151', marginLeft:8 }}>CST {r.ibs08_cst} · cClassTrib {r.ibs08_tri}</span>}
                </div>
                <div className="nat-item-dsc">{r.ibs08_des}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
